import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Building2, 
  ArrowLeft, 
  Save, 
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  LogOut,
  ChevronDown,
  X,
  Landmark
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierProfile = () => {
  const { supplier, token, logout, refreshSupplier } = useSupplierAuth();
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sectorPopoverOpen, setSectorPopoverOpen] = useState(false);
  const [areaPopoverOpen, setAreaPopoverOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      company_name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      sectors: [],
      areas_covered: [],
      bank_account_name: '',
      bank_sort_code: '',
      bank_account_number: '',
    },
  });

  const selectedSectors = form.watch('sectors') || [];
  const selectedAreas = form.watch('areas_covered') || [];

  const fetchData = useCallback(async () => {
    try {
      const [sectorsRes, areasRes] = await Promise.all([
        axios.get(`${API}/supplier-portal/sectors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/supplier-portal/areas`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSectors(sectorsRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (supplier) {
      form.reset({
        company_name: supplier.company_name || '',
        contact_person: supplier.contact_person || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        sectors: supplier.sectors || [],
        areas_covered: supplier.areas_covered || [],
        bank_account_name: supplier.bank_account_name || '',
        bank_sort_code: supplier.bank_sort_code || '',
        bank_account_number: supplier.bank_account_number || '',
      });
    }
  }, [supplier, form]);

  const toggleSector = (sector) => {
    const current = form.getValues('sectors') || [];
    if (current.includes(sector)) {
      form.setValue('sectors', current.filter(s => s !== sector));
    } else {
      form.setValue('sectors', [...current, sector]);
    }
  };

  const removeSector = (sector) => {
    const current = form.getValues('sectors') || [];
    form.setValue('sectors', current.filter(s => s !== sector));
  };

  const toggleArea = (area) => {
    const current = form.getValues('areas_covered') || [];
    if (current.includes(area)) {
      form.setValue('areas_covered', current.filter(a => a !== area));
    } else {
      form.setValue('areas_covered', [...current, area]);
    }
  };

  const removeArea = (area) => {
    const current = form.getValues('areas_covered') || [];
    form.setValue('areas_covered', current.filter(a => a !== area));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await axios.put(`${API}/supplier-portal/me`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSupplier();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold">NECOS.FM</h1>
              <p className="text-xs text-muted-foreground">Supplier Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/supplier-portal/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Link to="/supplier-portal/documents">
              <Button variant="ghost" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Documents
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold">Company Profile</h2>
          <p className="text-muted-foreground mt-1">
            Update your company information and contact details
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Company Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="company-name-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="address-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sectors */}
                  <FormField
                    control={form.control}
                    name="sectors"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sectors</FormLabel>
                        <Popover open={sectorPopoverOpen} onOpenChange={setSectorPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between font-normal",
                                selectedSectors.length === 0 && "text-muted-foreground"
                              )}
                            >
                              {selectedSectors.length === 0 
                                ? "Select sectors..." 
                                : `${selectedSectors.length} selected`}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <div className="max-h-[300px] overflow-auto p-2">
                              {sectors.map((sector) => (
                                <div
                                  key={sector}
                                  className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                  onClick={() => toggleSector(sector)}
                                >
                                  <Checkbox checked={selectedSectors.includes(sector)} />
                                  <span className="text-sm">{sector}</span>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {selectedSectors.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedSectors.map((sector) => (
                              <Badge key={sector} variant="secondary" className="pl-2 pr-1 py-1">
                                {sector}
                                <button type="button" onClick={() => removeSector(sector)} className="ml-1 hover:bg-muted rounded-full p-0.5">
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Areas Covered */}
                  <FormField
                    control={form.control}
                    name="areas_covered"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Area's Covered</FormLabel>
                        <Popover open={areaPopoverOpen} onOpenChange={setAreaPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between font-normal",
                                selectedAreas.length === 0 && "text-muted-foreground"
                              )}
                            >
                              {selectedAreas.length === 0 
                                ? "Select areas..." 
                                : `${selectedAreas.length} selected`}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <div className="max-h-[300px] overflow-auto p-2">
                              {areas.map((area) => (
                                <div
                                  key={area}
                                  className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                  onClick={() => toggleArea(area)}
                                >
                                  <Checkbox checked={selectedAreas.includes(area)} />
                                  <span className="text-sm">{area}</span>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {selectedAreas.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedAreas.map((area) => (
                              <Badge key={area} variant="secondary" className="pl-2 pr-1 py-1">
                                {area}
                                <button type="button" onClick={() => removeArea(area)} className="ml-1 hover:bg-muted rounded-full p-0.5">
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="contact_person"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="contact-person-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} data-testid="email-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="phone-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Banking Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-primary" />
                  Banking Details
                </CardTitle>
                <CardDescription>Optional - for payment processing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="bank_account_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Acme Corp Ltd" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="bank_sort_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sort Code</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., 12-34-56" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="bank_account_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., 12345678" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button type="submit" disabled={loading} data-testid="save-profile-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </div>
  );
};

export default SupplierProfile;
